/**
 * Shown on every page while a platform operator previews a client (SPEC decision 22): whose workspace this is, that it
 * is read-only (the API refuses every write), and the way back to Hangar administration.
 */
import type { Me } from '@hangar/shared';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/Icon';
import { previewTarget } from '@/api/orgSelection';
import { useOrgActions } from './useOrgActions';

export function PreviewBanner({ me }: { me: Me | undefined }) {
  const { exitPreview } = useOrgActions();
  if (!me?.preview) return null;
  const suspended = previewTarget()?.suspended === true;
  return (
    <div role="status" data-testid="preview-banner" className="sticky top-0 z-40 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm">
      <Icon name="visibility" size={18} className="text-amber-600 dark:text-amber-400" />
      <p className="min-w-0 flex-1">
        <span className="font-semibold text-foreground">Previewing {me.org.name}</span>
        <span className="text-muted-foreground"> as its team sees it. Read-only: nothing you do here changes their workspace.</span>
        {suspended ? (
          <span data-testid="preview-banner-suspended" className="mt-1 flex items-start gap-1.5 font-medium text-awaiting">
            <Icon name="pause_circle" size={16} className="mt-px" />
            Suspended: its team cannot use Hangar until you reactivate it from Clients.
          </span>
        ) : null}
      </p>
      <Button size="sm" variant="outline" data-testid="preview-exit" onClick={exitPreview}>
        Exit preview
      </Button>
    </div>
  );
}
